/**
 * @fileoverview Cold Room Heat Loss Simulation Module
 * 
 * This module provides an interactive educational experience for understanding
 * heat transfer mechanisms, thermodynamic equilibrium, and building heat loss.
 * It uses animations and visualizations to teach fundamental thermal concepts.
 * 
 * @module CRHeatLoss
 * @requires react
 * @requires antd
 * @requires react-router-dom
 * 
 * @description
 * The simulation progresses through four educational steps:
 * 1. Thermodynamics Equilibrium - Demonstrates 2nd law with particle animation
 * 2. Heat Escape Paths - Visualizes heat loss through building envelope
 * 3. Heat Conduction - Explains Fourier's law and material properties
 * 4. Heat Convection - Covers convective heat transfer and boundary layers
 * 
 * Each step builds upon previous concepts to create a comprehensive
 * understanding of building thermal performance.
 * 
 * @author HeatCraftHP Team
 * @version 1.0.0
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Button, Space } from 'antd';
import { RedoOutlined, FilePdfOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';

// Page components

// Shared components
import {
  HPTitle,
  NamedItemComponent,
  BrownianElement,
  SvgBoxWithStrips,
  RoomDiagram,
  WindowRect,
  MrHP,
  QoutationElement,
  CourseNavigationModal,
} from '../../../Shared';

// Other shared components
import { LatexRenderer } from '../../../Shared/LatexRenderer';

// Constants
import {
  FIRST_STEP,
  SECOND_STEP,
  THIRD_STEP,
  FOURTH_STEP,
  COLOR_UPDATE_INTERVAL_MS,
  MAX_COLOR_VALUE,
} from './constants';

// PDF Documents
import HeatFlowDocument from './HP_Why heat goes from warm to cold.pdf';
import RoomLossesDocument from './HP_room_losses.pdf';
import HeatTransferFundamentals from './HP_heat_transfer_fundamentals.pdf';
import PagesWrapper from '../../../../PagesWrapper';
import { courseStructure } from '../../Intro/courseStructure';

/**
 * Educational resource documents configuration
 * @constant {Array<{name: string, document: string}>}
 * @description PDF resources covering fundamental heat transfer concepts,
 * thermodynamic principles, and practical heat loss calculations for buildings
 */
const RESOURCES = [
  {
    name: 'Why heat flows from warm to cold',
    document: HeatFlowDocument,
  },
  {
    name: 'Means of heat transfer',
    document: HeatTransferFundamentals,
  },
  {
    name: 'Typical room losses',
    document: RoomLossesDocument,
  },
];

/**
 * Main heat loss simulation component
 * 
 * Orchestrates a four-step interactive learning experience about heat transfer
 * mechanisms in buildings, using animations, diagrams, and mathematical formulas.
 * 
 * @component
 * @returns {JSX.Element} Complete heat loss simulation page with step navigation
 * 
 * @description
 * Core features:
 * - Four progressive educational steps
 * - Animated particle simulation for thermodynamic concepts
 * - Interactive room diagrams with heat flow visualization
 * - Mathematical formula presentations with LaTeX rendering
 * - Course navigation modal integration
 * - PDF resource library access
 * - Color-animated equilibrium demonstration
 * 
 * State management:
 * - Current simulation step tracking
 * - Particle color animation (0-255 RGB values)
 * - Animation equilibrium detection
 * - Modal visibility control
 * 
 * @example
 * // Usage in routes
 * <Route path="/az-course/heat-loss" element={<CRHeatLoss />} />
 */
export function CRHeatLoss() {
  const location = useLocation();
  const [simulationStep, setSimulationStep] = useState(FIRST_STEP);
  const [firstColor, setFirstColor] = useState(0);
  const [secondColor, setSecondColor] = useState(0);
  const [showCourseNav, setShowCourseNav] = useState(false);

  // Color animation effect
  useEffect(() => {
    const interval = setInterval(() => {
      setFirstColor((prev) => Math.min(MAX_COLOR_VALUE, prev + 1));
      setSecondColor((prev) => Math.min(MAX_COLOR_VALUE, prev + 1));
    }, COLOR_UPDATE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  // Computed color values
  const colorProgress = firstColor / MAX_COLOR_VALUE;
  const getFirstColor = useCallback(
    () => `rgb(255, 0, ${firstColor})`,
    [firstColor]
  );
  const getSecondColor = useCallback(
    () => `rgb(${secondColor}, 0, 255)`,
    [secondColor]
  );
  const isEquilibrium = firstColor >= MAX_COLOR_VALUE;

  // Reset simulation
  const handleReset = useCallback(() => {
    setFirstColor(0);
    setSecondColor(0);
  }, []);

  // Navigation handlers
  const goToStep = useCallback((step) => {
    setSimulationStep(step);
  }, []);

  // Render step content based on current step
  const renderCurrentStep = () => {
    switch (simulationStep) {
      case FIRST_STEP:
        return (
          <FirstStep
            colorProgress={colorProgress}
            firstColor={firstColor}
            getFirstColor={getFirstColor}
            getSecondColor={getSecondColor}
            isEquilibrium={isEquilibrium}
            onNext={() => goToStep(SECOND_STEP)}
            onReset={handleReset}
          />
        );
      case SECOND_STEP:
        return (
          <SecondStep
            onPrevious={() => goToStep(FIRST_STEP)}
            onNext={() => goToStep(THIRD_STEP)}
          />
        );
      case THIRD_STEP:
        return (
          <ThirdStep
            onPrevious={() => goToStep(SECOND_STEP)}
            onNext={() => goToStep(FOURTH_STEP)}
          />
        );
      case FOURTH_STEP:
        return <FourthStep onPrevious={() => goToStep(THIRD_STEP)} />;
      default:
        return null;
    }
  };

  return (
    <PagesWrapper>
      <HPTitle 
        name="1. Cold room" 
        subname="1.1 Heat loss"
        onTitleClick={() => setShowCourseNav(true)}
      >
        <Space direction="vertical" size="small" align="start">
          <small className="default-gray">Resources</small>
          <Space>
            {RESOURCES.map((resource) => (
              <NamedItemComponent
                key={resource.name}
                name={resource.name}
                onClick={() => window.open(resource.document)}
              >
                <FilePdfOutlined className="default-red default-x-larger hq-clickable" />
              </NamedItemComponent>
            ))}
          </Space>
        </Space>
      </HPTitle>

      <CourseNavigationModal
        open={showCourseNav}
        onClose={() => setShowCourseNav(false)}
        courseStructure={courseStructure}
        currentPath={location.pathname}
      />

      <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
        <Space align="start">{renderCurrentStep()}</Space>
      </div>

    </PagesWrapper>
  );
}

// ============================================================================
// Step Components
// ============================================================================

/**
 * Mascot display section
 * 
 * Renders the HeatCraftHP mascot character in a standardized SVG container.
 * Provides visual consistency and engagement throughout the learning experience.
 * 
 * @component
 * @returns {JSX.Element} SVG container with positioned mascot character
 * 
 * @description
 * Fixed dimensions:
 * - Width: 150px
 * - Height: 410px
 * - Left margin: 50px for alignment
 * - Mascot width: 135px
 * 
 * The mascot serves as a friendly guide through the simulation steps.
 */
function MrHPSection() {
  return (
    <div>
      <svg
        width={150}
        height={410}
        xmlns="http://www.w3.org/2000/svg"
        style={{ marginLeft: 50 }}
      >
        <MrHP y={0} x={0} width={135} />
      </svg>
    </div>
  );
}

/**
 * Navigation button group
 * 
 * Reusable navigation controls for moving between simulation steps.
 * Displays Previous and/or Next buttons based on provided callbacks.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Function} [props.onPrevious] - Callback for previous button (optional)
 * @param {Function} [props.onNext] - Callback for next button (optional)
 * @returns {JSX.Element} Button group with appropriate navigation options
 * 
 * @description
 * Button behavior:
 * - Previous button shown only if onPrevious callback provided
 * - Next button shown only if onNext callback provided
 * - Buttons use Ant Design primary type styling
 * - Small size for compact display
 * 
 * @example
 * // Both buttons
 * <NavigationButtons
 *   onPrevious={() => goToStep(1)}
 *   onNext={() => goToStep(3)}
 * />
 * 
 * @example
 * // Only next button (first step)
 * <NavigationButtons onNext={() => goToStep(2)} />
 */
function NavigationButtons({ onPrevious, onNext }) {
  return (
    <Space>
      {onPrevious && (
        <Button size="small" type="primary" onClick={onPrevious}>
          Previous
        </Button>
      )}
      {onNext && (
        <Button size="small" type="primary" onClick={onNext}>
          Next
        </Button>
      )}
    </Space>
  );
}

/**
 * First step - Thermodynamic equilibrium demonstration
 * 
 * Animated visualization of the second law of thermodynamics using Brownian
 * motion particles. Demonstrates heat flow from hot to cold until equilibrium.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {number} props.colorProgress - Animation progress (0-1, normalized from firstColor)
 * @param {number} props.firstColor - Hot particle color intensity (0-255)
 * @param {Function} props.getFirstColor - Returns current hot particle RGB color
 * @param {Function} props.getSecondColor - Returns current cold particle RGB color
 * @param {boolean} props.isEquilibrium - True when animation reaches thermal equilibrium
 * @param {Function} props.onNext - Callback to navigate to next step
 * @param {Function} props.onReset - Callback to restart animation
 * @returns {JSX.Element} Animated particle simulation with thermometer visualization
 * 
 * @description
 * Visualization features:
 * - 80 hot particles (left side) - start fast, slow down
 * - 80 cold particles (right side) - start slow, speed up
 * - Particle speeds converge as temperatures equilibrate
 * - Color gradient: Red → Purple (hot) and Blue → Purple (cold)
 * - Dynamic heat flow arrow (disappears at equilibrium)
 * - Temperature indicators with thermometer visualization
 * - Reset button appears when equilibrium reached
 * 
 * Physics demonstrated:
 * - 2nd law of thermodynamics
 * - Heat always flows from hot to cold
 * - System reaches equilibrium when T1 = T2
 * - Particle kinetic energy represents temperature
 * 
 * @example
 * <FirstStep
 *   colorProgress={0.5}
 *   firstColor={128}
 *   getFirstColor={() => 'rgb(255,0,128)'}
 *   getSecondColor={() => 'rgb(128,0,255)'}
 *   isEquilibrium={false}
 *   onNext={() => {}}
 *   onReset={() => {}}
 * />
 */
function FirstStep({
  colorProgress,
  firstColor,
  getFirstColor,
  getSecondColor,
  isEquilibrium,
  onNext,
  onReset,
}) {
  const arrowLength = 100 + colorProgress * 50;

  // Generate random positions for particles (memoized to prevent re-generation)
  const particles1 = useMemo(
    () =>
      Array(80)
        .fill(0)
        .map(() => ({
          x: 10 + Math.random() * 135,
          y: 10 + Math.random() * 135,
        })),
    []
  );

  const particles2 = useMemo(
    () =>
      Array(80)
        .fill(0)
        .map(() => ({
          x: 150 + Math.random() * 135,
          y: 10 + Math.random() * 135,
        })),
    []
  );

  return (
    <div>
      <Space align="start">
        <RoomDiagram>
          <WindowRect />
        </RoomDiagram>

        <MrHPSection />

        <div style={{ width: '30vw' }}>
          <QoutationElement
            texts={[
              'Heat loss happens because heat always flows from warm to cold, seeking Equilibrium.',
            ]}
          >
            <small className="default-gray">2nd law of thermodynamics</small>
            <br />
            <Button size="small" type="primary" onClick={onNext}>
              Next
            </Button>
          </QoutationElement>
        </div>

        <div>
          <svg width={420} height={350} stroke="gray" strokeWidth={1}>
            {/* Hot particles (left side) */}
            {particles1.map((pos, i) => (
              <BrownianElement
                key={`hot-${i}`}
                Speed={1.75 - 1.25 * colorProgress}
                X={pos.x}
                Y={pos.y}
                minX={0}
                maxX={145}
                minY={0}
                maxY={145}
                R={5}
                Color={getFirstColor()}
              />
            ))}

            {/* Cold particles (right side) */}
            {particles2.map((pos, i) => (
              <BrownianElement
                key={`cold-${i}`}
                Speed={0.25 + 0.25 * colorProgress}
                X={pos.x}
                Y={pos.y}
                minX={150}
                maxX={295}
                minY={0}
                maxY={145}
                R={5}
                Color={getSecondColor()}
              />
            ))}

            {/* Heat flow arrow */}
            {!isEquilibrium && (
              <path
                d={`M${arrowLength},60 L200,60 L200,50 L220,65 L200,80 L200,70 L${arrowLength},70 Z`}
                fill="rgba(0,255,0,0.8)"
                stroke="black"
                strokeWidth={2}
              />
            )}

            {/* Container box */}
            <rect
              x={0}
              y={0}
              width={300}
              height={150}
              stroke="#0B75BD"
              strokeWidth={2}
              fill="transparent"
            />

            {/* Labels */}
            <text x={80} y={170} fontSize="12px" stroke="#0B75BD">
              1
            </text>
            <text x={230} y={170} fontSize="12px" stroke="#0B75BD">
              2
            </text>
            <text x={90} y={200} fontSize="14px" stroke="#0B75BD">
              {isEquilibrium ? 'System in Equilibrium' : 'Heat goes from 1 to 2'}
            </text>

            {/* Temperature indicators */}
            <g transform={`translate(330, ${70 * colorProgress})`}>
              <text fontSize="12px" x={0} y={15} stroke={getFirstColor()}>
                T
              </text>
              <text x={10} y={20} fontSize="9px" stroke={getFirstColor()}>
                1
              </text>
            </g>

            <g transform={`translate(400, ${140 - 70 * colorProgress})`}>
              <text fontSize="12px" x={0} y={15} stroke={getSecondColor()}>
                T
              </text>
              <text x={10} y={20} fontSize="9px" stroke={getSecondColor()}>
                2
              </text>
            </g>

            {/* Thermometer */}
            <rect
              x={368}
              y={0}
              height={150}
              width={4}
              fill="rgba(0,0,0,0.2)"
              stroke="rgba(0,0,0,0.4)"
            />
            <circle
              cx={370}
              cy={160}
              r={10}
              fill="rgba(0,0,0,0.2)"
              stroke="rgba(0,0,0,0.4)"
            />
            <rect
              x={350}
              y={70 * colorProgress + 10}
              height={1}
              width={20}
              fill="rgba(0,0,0,0.2)"
            />
            <rect
              x={370}
              y={140 - 70 * colorProgress + 10}
              height={1}
              width={25}
              fill="rgba(0,0,0,0.2)"
            />
          </svg>
        </div>

        {isEquilibrium && (
          <RedoOutlined
            className="default-title default-x-larger hq-clickable"
            title="Redo experiment"
            onClick={onReset}
          />
        )}
      </Space>
    </div>
  );
}


/**
 * Second step - Heat escape paths visualization
 * 
 * Displays a room diagram with animated arrows showing all the paths
 * through which heat escapes from a building to the outdoor environment.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.onPrevious - Callback to navigate to previous step
 * @param {Function} props.onNext - Callback to navigate to next step
 * @returns {JSX.Element} Room diagram with heat loss arrows and explanation
 * 
 * @description
 * Heat loss paths visualized:
 * - Wall heat loss (right side): 5 arrows showing conduction through walls
 * - Wall heat loss (left side): 3 arrows (1 top, 2 bottom)
 * - Window heat loss: 5 arrows showing higher loss rate through glazing
 * 
 * Key concepts:
 * - Multiple escape routes for heat in buildings
 * - Heat transfer via conduction, convection, and ventilation
 * - Temperature difference drives heat loss rate
 * - Poor insulation increases heat loss
 * 
 * Visual design:
 * - Red arrows indicate heat flow direction (indoor → outdoor)
 * - Arrow density suggests relative heat loss intensity
 * - Room diagram shows realistic building cross-section
 */
function SecondStep({ onPrevious, onNext }) {
  return (
    <div>
      <Space align="start">
        <RoomDiagram>
          <WindowRect />

          {/* Wall heat arrows (right) */}
          {Array(5)
            .fill(0)
            .map((_, i) => (
              <path
                key={`right-${i}`}
                d={`M295,${60 + 50 * i} L340,${60 + 50 * i} L340,${55 + 50 * i} L350,${62.5 + 50 * i} L340,${70 + 50 * i} L340,${65 + 50 * i} L295,${65 + 50 * i} Z`}
                fill="red"
                stroke="none"
              />
            ))}

          {/* Wall heat arrows (left top) */}
          <path
            d="M80,60 L25,60 L25,55 L15,62.5 L25,70 L25,65 L80,65 Z"
            fill="red"
            stroke="none"
          />

          {/* Wall heat arrows (left bottom) */}
          {Array(2)
            .fill(0)
            .map((_, i) => (
              <path
                key={`left-bottom-${i}`}
                d={`M80,${210 + 50 * i} L25,${210 + 50 * i} L25,${205 + 50 * i} L15,${212.5 + 50 * i} L25,${220 + 50 * i} L25,${215 + 50 * i} L80,${215 + 50 * i} Z`}
                fill="red"
                stroke="none"
              />
            ))}

          {/* Window heat arrows */}
          {Array(5)
            .fill(0)
            .map((_, i) => (
              <path
                key={`window-${i}`}
                d={`M95,${125 + 10 * i} L70,${125 + 10 * i} L70,${123 + 10 * i} L65,${127 + 10 * i} L70,${131 + 10 * i} L70,${129 + 10 * i} L95,${129 + 10 * i} Z`}
                fill="red"
                stroke="none"
              />
            ))}
        </RoomDiagram>

        <MrHPSection />

        <div>
          <QoutationElement
            texts={[
              'Heat escapes to the outdoor through walls, windows, and gaps mainly via conduction, convection and ventilation.',
              'The bigger the temperature difference and the poorer the insulation, the faster the loss.',
            ]}
          >
            <br />
            <NavigationButtons onPrevious={onPrevious} onNext={onNext} />
          </QoutationElement>
        </div>
      </Space>
    </div>
  );
}

/**
 * Third step - Heat conduction explanation
 * 
 * Comprehensive introduction to heat conduction using Fourier's law,
 * including mathematical formulas, variable definitions, and material
 * property comparisons.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.onPrevious - Callback to navigate to previous step
 * @param {Function} props.onNext - Callback to navigate to next step
 * @returns {JSX.Element} Educational content on heat conduction with diagrams
 * 
 * @description
 * Educational content:
 * - Heat conduction definition and mechanism
 * - Fourier's law formula: Q̇_cond = -λ · A · (dT/dx)
 * - Variable explanations:
 *   - λ: Thermal conductivity [W/(m·K)]
 *   - A: Conduction area [m²]
 *   - dT/dx: Temperature gradient [K/m]
 * - Material property comparisons
 * - Building insulation applications
 * 
 * Visualizations:
 * - Temperature gradient line through wall
 * - Wall temperature labels (T_wall,in and T_wall,out)
 * - Conductivity comparison diagram (2 rooms)
 * - Room 1: Concrete + EPS insulation (λ1 << λ2)
 * - Room 2: Concrete only (higher thermal conductivity)
 * 
 * The side-by-side comparison demonstrates how insulation (lower λ)
 * dramatically reduces heat transfer compared to concrete alone.
 */
function ThirdStep({ onPrevious, onNext }) {
  return (
    <div>
      <Space align="start">
        <RoomDiagram>
          <WindowRect />

          {/* Temperature gradient line */}
          <line x1={311} y1={90} x2={329} y2={220} stroke="red" strokeWidth={3} />
          <line
            x1={280}
            y1={90}
            x2={360}
            y2={90}
            stroke="red"
            strokeWidth={1}
            strokeDasharray="3"
          />
          <line
            x1={280}
            y1={220}
            x2={360}
            y2={220}
            stroke="red"
            strokeWidth={1}
            strokeDasharray="3"
          />

          {/* Temperature labels */}
          <text x={255} y={105} fill="red" fontSize="14px">
            T
          </text>
          <text x={263} y={110} fill="red" fontSize="12px">
            wall, in
          </text>
          <text x={350} y={237} fill="red" fontSize="14px">
            T
          </text>
          <text x={358} y={242} fill="red" fontSize="12px">
            wall, out
          </text>
        </RoomDiagram>

        <MrHPSection />

        <div>
          <QoutationElement
            texts={[
              'Heat conduction is the transfer of thermal energy through a material, driven by a temperature difference, without the material itself moving.',
              '\n',
              "It occurs as atoms or molecules collide, passing kinetic energy from hotter to cooler regions. The rate depends on the material's thermal conductivity λ, temperature gradient, and geometry (thickness δ, area A).",
              '\n',
              'Metals conduct heat well, while insulators like mineral wool or EPS (Expanded Polystyrene) have lower thermal conductivity and are used to reduce heat transfer in buildings.',
            ]}
          >
            <p className="default-gray">
              Heat conduction formula (Fourier's law):
            </p>
            <LatexRenderer
              latex={'$\\dot{Q}_{cond} = - \\lambda \\cdot A \\cdot \\large{\\frac{dT}{dx}}$'}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'start' }}>
              <p className="default-gray">Where,</p>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'max-content 1fr max-content',
                  columnGap: 16,
                  rowGap: 8,
                  alignItems: 'start',
                }}
              >
                <FormulaVariable
                  symbol="$\lambda$"
                  description="The thermal conductivity"
                  unit="$\frac{W}{m \cdot K}$"
                />
                <FormulaVariable
                  symbol="$A$"
                  description="The conduction area"
                  unit="$m^{2}$"
                />
                <FormulaVariable
                  symbol="$\large{\frac{dT}{dx}}$"
                  description="Temperature gradient"
                  unit="$\frac{K}{m}$"
                />
              </div>
            </div>
            <br />
            <NavigationButtons onPrevious={onPrevious} onNext={onNext} />
          </QoutationElement>
        </div>

        <ConductivityComparisonDiagram />
      </Space>
    </div>
  );
}

/**
 * Formula variable display component for grid layouts
 * 
 * Renders a single formula variable as three aligned grid cells containing
 * the mathematical symbol, description, and unit. Optimized for use in
 * CSS Grid layouts with 'max-content 1fr max-content' column template.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.symbol - LaTeX mathematical symbol (e.g., "$\\lambda$")
 * @param {string} props.description - Plain text description of the variable
 * @param {string} props.unit - LaTeX unit expression (e.g., "$\\frac{W}{m \\cdot K}$")
 * @returns {React.Fragment} Three div elements for grid cell alignment
 * 
 * @description
 * Grid cell structure:
 * 1. Symbol cell (max-content): Right-padded LaTeX symbol
 * 2. Description cell (1fr): Left-aligned gray text, flexible width
 * 3. Unit cell (max-content): Left-padded LaTeX unit
 * 
 * @example
 * <div style={{display: 'grid', gridTemplateColumns: 'max-content 1fr max-content'}}>
 *   <FormulaVariable
 *     symbol="$\\alpha$"
 *     description="The convective heat transfer coefficient"
 *     unit="$\\frac{W}{m^{2} \\cdot K}$"
 *   />
 * </div>
 */
function FormulaVariable({ symbol, description, unit }) {
  // Render three cells so when placed inside a grid each variable lines up in columns
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', paddingRight: 4 }}>
        <LatexRenderer latex={symbol} />
      </div>

      <div>
        <p className="default-gray" style={{ margin: 0, textAlign:'left' }}>{description}</p>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', paddingLeft: 4 }}>
        <LatexRenderer latex={unit} />
      </div>
    </>
  );
}

/**
 * Thermal conductivity comparison diagram
 * 
 * Side-by-side visualization comparing heat conduction through two different
 * wall assemblies: insulated (concrete + EPS) vs. non-insulated (concrete only).
 * 
 * @component
 * @returns {JSX.Element} SVG diagram showing two rooms with different wall types
 * 
 * @description
 * Room 1 (Left) - Insulated wall:
 * - Material: Concrete + EPS (Expanded Polystyrene)
 * - Thermal conductivity: λ1 (low)
 * - Temperature gradient: Gentle slope (T1 → T_outdoor)
 * - Heat loss: Low
 * 
 * Room 2 (Right) - Non-insulated wall:
 * - Material: Concrete only
 * - Thermal conductivity: λ2 (high)
 * - Temperature gradient: Steep slope (T2 → T_outdoor)
 * - Heat loss: High
 * 
 * Visual elements:
 * - Striped wall patterns (45° angle)
 * - Temperature gradient lines (red, varying slopes)
 * - Temperature labels at wall surfaces
 * - Shared outdoor temperature reference line
 * - Mathematical comparison: λ1 << λ2
 * 
 * The diagram clearly illustrates how lower thermal conductivity
 * (better insulation) reduces the temperature drop through the wall,
 * thereby reducing heat loss.
 */
function ConductivityComparisonDiagram() {
  return (
    <div>
      <svg width={300} height={230} style={{marginLeft:10}} xmlns="http://www.w3.org/2000/svg">
        {/* Room 1 - Insulated wall */}
        <SvgBoxWithStrips W={20} H={150} X={30} Y={20} angle={45} patternId="cond-left" />
        <line x1={41} y1={40} x2={59} y2={150} stroke="red" strokeWidth={3} />
        <line
          x1={11}
          y1={40}
          x2={89}
          y2={40}
          stroke="red"
          strokeWidth={1}
          strokeDasharray="3"
        />
        <line x1={0} y1={30} x2={41} y2={30} stroke="black" strokeWidth={1} />
        <line x1={0} y1={180} x2={41} y2={180} stroke="black" strokeWidth={1} />

        <text x={0} y={18} fill="#0B75BD" fontSize="12px">
          Room 1
        </text>
        <text x={10} y={55} fill="red" fontSize="14px">
          T
        </text>
        <text x={18} y={60} fill="red" fontSize="12px">
          1
        </text>
        <text x={30} y={200} fill="#0B75BD" fontSize="16px">
          λ
        </text>
        <text x={40} y={205} fill="#0B75BD" fontSize="12px">
          1
        </text>
        <text x={0} y={220} fill="#0B75BD" fontSize="12px">
          Concrete + EPS
        </text>

        {/* Room 2 - Non-insulated wall */}
        <SvgBoxWithStrips W={20} H={150} X={180} Y={20} angle={45} patternId="cond-right" />
        <line x1={191} y1={100} x2={209} y2={150} stroke="red" strokeWidth={3} />
        <line
          x1={161}
          y1={100}
          x2={239}
          y2={100}
          stroke="red"
          strokeWidth={1}
          strokeDasharray="3"
        />
        <line x1={150} y1={30} x2={191} y2={30} stroke="black" strokeWidth={1} />
        <line x1={150} y1={180} x2={191} y2={180} stroke="black" strokeWidth={1} />

        <text x={150} y={18} fill="#0B75BD" fontSize="12px">
          Room 2
        </text>
        <text x={160} y={115} fill="red" fontSize="14px">
          T
        </text>
        <text x={168} y={120} fill="red" fontSize="12px">
          2
        </text>
        <text x={180} y={200} fill="#0B75BD" fontSize="16px">
          λ
        </text>
        <text x={190} y={205} fill="#0B75BD" fontSize="12px">
          2
        </text>
        <text x={150} y={220} fill="#0B75BD" fontSize="12px">
          Concrete only
        </text>

        {/* Outdoor temperature line */}
        <line
          x1={0}
          y1={150}
          x2={300}
          y2={150}
          stroke="red"
          strokeWidth={1}
          strokeDasharray="3"
        />
        <text x={240} y={165} fill="red" fontSize="14px">
          T
        </text>
        <text x={248} y={170} fill="red" fontSize="12px">
          outdoor
        </text>

        {/* Conductivity comparison */}
        <text x={250} y={220} fill="#0B75BD" fontSize="16px">
          λ
        </text>
        <text x={258} y={225} fill="#0B75BD" fontSize="12px">
          1
        </text>
        <text x={265} y={220} fill="#0B75BD" fontSize="12px">
          {'<<'}
        </text>
        <text x={285} y={220} fill="#0B75BD" fontSize="16px">
          λ
        </text>
        <text x={293} y={225} fill="#0B75BD" fontSize="12px">
          2
        </text>
      </svg>
    </div>
  );
}

/**
 * Fourth step - Heat convection explanation
 * 
 * Final step introducing convective heat transfer, boundary layers,
 * and the role of convection in building heat loss.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.onPrevious - Callback to navigate to previous step
 * @returns {JSX.Element} Educational content on heat convection
 * 
 * @description
 * Educational content:
 * - Convection definition and mechanism
 * - Natural vs. forced convection
 * - Convection in buildings (air circulation, drafts)
 * - Boundary layer effects on exterior walls
 * - Heat transfer coefficient formula: Q̇_conv = -α · A · (T_surface - T_outside)
 * - Variable explanations:
 *   - α: Convective heat transfer coefficient [W/(m²·K)]
 *   - A: Convection area [m²]
 * 
 * Factors affecting heat transfer coefficient:
 * - Air movement (wind speed/ventilation)
 * - Surface roughness
 * - Temperature difference
 * - Air properties (density, viscosity, thermal conductivity)
 * 
 * Visualizations:
 * - Faded conduction elements (from previous step)
 * - Highlighted convection boundary layers
 * - Curved convection paths showing fluid flow
 * - Temperature labels at room, wall surface, and outdoor
 * 
 * This step completes the heat transfer fundamentals by showing how
 * convection at wall surfaces contributes to overall building heat loss.
 */
function FourthStep({ onPrevious }) {
  return (
    <div>
      <Space align="start">
        <RoomDiagram>
          <WindowRect />

          {/* Faded conduction lines */}
          <line
            x1={311}
            y1={90}
            x2={329}
            y2={220}
            stroke="red"
            strokeWidth={3}
            opacity={0.3}
          />
          <line
            x1={280}
            y1={90}
            x2={360}
            y2={90}
            stroke="red"
            strokeWidth={1}
            strokeDasharray="3"
            opacity={0.5}
          />
          <line
            x1={280}
            y1={220}
            x2={360}
            y2={220}
            stroke="red"
            strokeWidth={1}
            strokeDasharray="3"
            opacity={0.5}
          />

          {/* Faded wall temperature labels */}
          <text x={255} y={105} fill="red" fontSize="14px" opacity={0.5}>
            T
          </text>
          <text x={263} y={110} fill="red" fontSize="12px" opacity={0.5}>
            wall, in
          </text>
          <text x={350} y={204} fill="red" fontSize="14px" opacity={0.5}>
            T
          </text>
          <text x={358} y={209} fill="red" fontSize="12px" opacity={0.5}>
            wall, out
          </text>

          {/* Convection boundary lines */}
          <line
            x1={260}
            y1={75}
            x2={360}
            y2={75}
            stroke="red"
            strokeWidth={1}
            strokeDasharray="3"
          />
          <line
            x1={280}
            y1={235}
            x2={380}
            y2={235}
            stroke="red"
            strokeWidth={1}
            strokeDasharray="3"
          />

          {/* Convection curves */}
          <path
            fill="none"
            stroke="red"
            strokeWidth="3"
            d="M 331,220 A 20,20 0 0,0 351,235"
          />
          <line x1={351} y1={235} x2={375} y2={235} stroke="red" strokeWidth={3} />
          <text x={350} y={252} fill="red" fontSize="14px">
            T
          </text>
          <text x={358} y={257} fill="red" fontSize="12px">
            outdoor
          </text>

          <path
            fill="none"
            stroke="red"
            strokeWidth="3"
            d="M 309,90 A 20,20 0 0,0 289,75"
          />
          <line x1={289} y1={75} x2={265} y2={75} stroke="red" strokeWidth={3} />
          <text x={255} y={60} fill="red" fontSize="14px">
            T
          </text>
          <text x={263} y={65} fill="red" fontSize="12px">
            room
          </text>
        </RoomDiagram>

        <MrHPSection />

        <div>
          <QoutationElement
            texts={[
              'Heat convection is the transfer of heat through the movement of fluids, like air or water, driven by temperature differences. It occurs naturally, as warm fluid rises and cool fluid sinks, or can be forced using fans or pumps.',
              '\n',
              'In buildings, convection affects how heat circulates—warm air rises, creating drafts and temperature variations unless managed with proper insulation and ventilation.',
              '\n',
              'Convection is the means through which heat is lost to the outside environment, as the cold air cools the external walls of a building.',
              '\n',
              'The heat transfer coefficient α between building walls and air is influenced by air movement (wind speed/ventilation), surface roughness, temperature difference, and air properties.',
            ]}
          >
            <br />
            <p className="default-gray">Heat convection formula:</p>
            <LatexRenderer
              latex={
                '$\\dot{Q}_{conv} = - \\alpha \\cdot A \\cdot (T_{surface} - T_{outside})$'
              }
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems:'start' }}>
              <p className="default-gray">Where,</p>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'max-content 1fr max-content',
                  columnGap: 16,
                  rowGap: 8,
                  alignItems: 'start',
                }}
              >
                <FormulaVariable
                  symbol="$\alpha$"
                  description="The convective heat transfer coefficient"
                  unit="$\frac{W}{m^{2} \cdot K}$"
                />
                <FormulaVariable
                  symbol="$A$"
                  description="The convection area"
                  unit="$m^{2}$"
                />
              </div>
            </div>
            <br />
            <NavigationButtons onPrevious={onPrevious} />
          </QoutationElement>
        </div>
      </Space>
    </div>
  );
}

export default CRHeatLoss;
